import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, isSameDay } from 'date-fns';
import { EXCHANGE_RATES } from './currency';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as a currency string
 * @param amount - The amount to format
 * @param currency - The currency code (default: EGP for Egyptian Pound)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EGP',
  locale: string = 'en-US'
): string {
  // Handle undefined, null, or NaN values
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '—';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a number as a currency string using the provided currency code
 * This version is designed to work with the currency context
 * @param amount - The amount to format
 * @param currencyCode - The currency code to use
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted currency string
 */
export function formatCurrencyWithCode(
  amount: number,
  currencyCode: string = 'EGP',
  locale: string = 'en-US'
): string {
  // Handle undefined, null, or NaN values
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '—';
  }

  const amountAfterExchangeRate = amount * parseFloat(EXCHANGE_RATES[currencyCode]);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountAfterExchangeRate);
}

export function formatCurrencyWithoutCode(amount: number, currencyCode: string = 'EGP'): number {
  const amountAfterExchangeRate = Math.round(amount * parseFloat(EXCHANGE_RATES[currencyCode]));

  return amountAfterExchangeRate;
}

/**
 * Converts a date to UTC midnight
 */
export function toUTCDate(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/**
 * Safely compares two dates to see if they represent the same calendar day,
 * regardless of timezone.
 */
export function isSameCalendarDay(date1: Date | string, date2: Date | string): boolean {
  // Use date-fns isSameDay which compares the year, month, and day components
  return isSameDay(
    typeof date1 === 'string' ? new Date(date1) : date1,
    typeof date2 === 'string' ? new Date(date2) : date2
  );
}

/**
 * For creating new BookableDate records
 */
export function createBookableUTCDate(date: Date | string): Date {
  return toUTCDate(date);
}

/**
 * For display formatting in UI
 */
export function formatDateForDisplay(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'EEE MMM dd yyyy');
}
